import dayjs from 'dayjs';

interface ReceiptFilenameSource {
  requisition_id?: string;
  submitted_at?: string;
  order_date?: string;
}

export function getReceiptPdfFilename(requisition?: ReceiptFilenameSource | null): string {
  const requisitionId = requisition?.requisition_id?.trim();
  const dateSource = requisition?.order_date || requisition?.submitted_at;
  const date = dateSource ? dayjs.utc(dateSource) : null;
  const datePart = date?.isValid() ? ` ${date.format('YYYY-MM-DD')}` : '';

  if (!requisitionId) {
    return `Order Receipt${datePart}.pdf`;
  }

  return `${requisitionId} - Order Receipt${datePart}.pdf`;
}

const escapeHtml = (value: string): string => {
  const container = document.createElement('div');
  container.textContent = value;
  return container.innerHTML;
};

const waitForImages = (frameDocument: Document): Promise<void[]> =>
  Promise.all(
    Array.from(frameDocument.images).map(
      image =>
        new Promise<void>(resolve => {
          if (image.complete) {
            resolve();
            return;
          }
          image.addEventListener('load', () => resolve(), { once: true });
          image.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  );

const waitForLayout = async (frameWindow: Window): Promise<void> => {
  await frameWindow.document.fonts?.ready;
  await waitForImages(frameWindow.document);
  void frameWindow.document.body.offsetHeight;
  await new Promise<void>(resolve => frameWindow.setTimeout(resolve, 0));
};

function printReceipt(element: HTMLElement, title: string): Promise<void> {
  const iframe = document.createElement('iframe');
  const receipt = element.cloneNode(true) as HTMLElement;
  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map(style => style.outerHTML)
    .join('');

  receipt.classList.add('receipt-pdf-rendering');
  iframe.title = 'Print order receipt';
  iframe.className = 'receipt-print-frame';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;

  return new Promise((resolve, reject) => {
    const previousDocumentTitle = document.title;
    let cleanupTimeout: number | undefined;
    let printing = false;
    let settled = false;

    const restoreDocumentTitle = () => {
      document.title = previousDocumentTitle;
    };

    const cleanup = () => {
      if (settled) return;
      settled = true;
      restoreDocumentTitle();
      window.removeEventListener('afterprint', cleanup);
      if (cleanupTimeout !== undefined) window.clearTimeout(cleanupTimeout);
      iframe.onload = null;
      iframe.remove();
      resolve();
    };

    iframe.onload = async () => {
      if (printing) return;
      printing = true;
      const frameWindow = iframe.contentWindow;
      if (!frameWindow) {
        settled = true;
        restoreDocumentTitle();
        iframe.remove();
        reject(new Error('Print frame failed to load'));
        return;
      }

      try {
        await waitForLayout(frameWindow);
        // Chrome/Safari Save as PDF uses the parent tab title, not the iframe <title>.
        document.title = title;
        cleanupTimeout = window.setTimeout(cleanup, 60_000);
        frameWindow.addEventListener('afterprint', cleanup, { once: true });
        window.addEventListener('afterprint', cleanup);
        frameWindow.focus();
        frameWindow.print();
      } catch (error) {
        settled = true;
        restoreDocumentTitle();
        window.removeEventListener('afterprint', cleanup);
        if (cleanupTimeout !== undefined) window.clearTimeout(cleanupTimeout);
        iframe.onload = null;
        iframe.remove();
        reject(error);
      }
    };

    iframe.srcdoc = `<!doctype html>
      <html>
        <head>
          <base href="${escapeHtml(document.baseURI)}">
          <title>${escapeHtml(title)}</title>
          ${styles}
          <style>
            @page {
              size: letter;
              margin: 0.5in 0.2in;
            }

            @media print {
              html,
              body {
                margin: 0;
                print-color-adjust: exact;
              }

              .requisition-receipt-content-wrapper {
                max-width: none;
              }
            }
          </style>
        </head>
        <body>${receipt.outerHTML}</body>
      </html>`;
    document.body.appendChild(iframe);
  });
}

// Chrome seeds the Save as PDF filename from the document title and appends .pdf itself.
export function printReceiptPdf(element: HTMLElement, filename = 'Order Receipt.pdf'): Promise<void> {
  return printReceipt(element, filename.replace(/\.pdf$/i, ''));
}
